import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mail,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Heart,
  Activity,
  FlaskConical,
  ShieldPlus,
  Brain,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import { useApi } from '../../hooks/useApi';
import PartnerSignIn from '../../components/PartnerSignIn/PartnerSignIn';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    execute: requestReset,
    loading,
    error,
  } = useApi(authService.requestPasswordReset);

  const features = [
    {
      icon: Activity,
      text: t('auth:features.healthRecords'),
    },
    {
      icon: ShieldPlus,
      text: t('auth:features.insurance'),
    },
    {
      icon: FlaskConical,
      text: t('auth:features.smartReports'),
    },
    {
      icon: Heart,
      text: t('auth:features.womensHealth'),
    },
    {
      icon: Brain,
      text: t('auth:features.aiAssessments'),
    },
  ];

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    const response = await requestReset({
      email,
    });

    if (response) {
      setSuccess(true);
    }
  };

  return (
    <div className="login-page">

      <div className="login-bg-shape shape-1" />
      <div className="login-bg-shape shape-2" />
      <div className="login-bg-shape shape-3" />

      <div className="login-container">

        {/* LEFT */}

        <div className="login-left">

          <div className="login-branding">

            <div className="login-logo-mark">
              <img src="/images/logo.svg" alt="Infinicare" style={{ height: 100 }} />
            </div>

            <p className="login-tagline"> {t('auth:tagline')} </p>

            <div className="login-features">

              {features.map((feature, index) => {

                const Icon = feature.icon;

                return (

                  <div key={index} className="feature-item" >

                    <div className="feature-icon-wrapper">
                      <Icon size={20} />
                    </div>

                    <span>{feature.text}</span>

                  </div>

                );

              })}

            </div>

          </div>

        </div>

        {/* RIGHT */}

        <div className="login-right">

          <div className="login-form-container">

            <button type="button" className="back-to-roles" onClick={() => navigate('/login')} > <ArrowLeft size={16} /> Back </button>

            <h2 className="login-title"> Forgot Password </h2>

            <p className="login-subtitle">
                Enter your registered email address and we'll send you a secure password reset link.
            </p>

            {success ? (
            <>
                <div className="login-success">
                    <CheckCircle2 size={20} />
                    <div>
                        <strong>Check your email</strong>

                        <p>
                            If an account exists for
                            <br />
                            <strong>{email}</strong>,
                            <br />
                            you will receive a password reset link shortly.
                            <br />
                            Please check your inbox and spam folder.
                        </p>
                    </div>
                </div>

                <div className="success-actions">
                    <p className="resend-text">
                        Didn't receive the email?{" "}
                        <button type="button" className="resend-link" onClick={() => { setSuccess(false); setEmail("");}}>
                            Try another email
                        </button>
                    </p>
                </div>
            </>
            ) : (

              <>
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">

                  <div className="form-group">

                    <label>Email Address</label>

                    <div className="input-wrapper">

                      <Mail size={18} className="input-icon" />

                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your registered email" autoComplete="email" autoFocus required />

                    </div>

                  </div>

                  <button type="submit" className="login-button" disabled={loading} >

                    {loading ? (

                      <span className="button-loading">

                        <Loader2 size={20} className="spinner" />

                        Sending...

                      </span>

                    ) : (

                      'Send Reset Link'

                    )}

                  </button>

                </form>

              </>
            )}

            <div className="login-footer">

              <p>

                Remember your password?{' '}

                <Link to="/login" className="register-link" >

                  Sign In

                </Link>

              </p>

            </div>

            <PartnerSignIn />

          </div>

        </div>

      </div>

    </div>

  );

};

export default ForgotPassword;